/**
 * Hook para funcionalidades PWA
 *
 * Gerencia detecção offline e atualização do service worker.
 * A instalação do app (beforeinstallprompt, iPhone) fica em `@utils/pwaInstall` (#291).
 *
 * @author Equipe VendeAi
 * @since 1.0.0
 */

import { useState, useEffect, useCallback } from 'react';

interface PWAState {
  isOnline: boolean;
  hasUpdate: boolean;
  isOfflineFallback: boolean;
}

export function usePWA() {
  const [state, setState] = useState<PWAState>({
    isOnline: navigator.onLine,
    hasUpdate: false,
    isOfflineFallback: false,
  });

  // Detecção online/offline
  useEffect(() => {
    const handleOnline = () => {
      setState(prev => ({ ...prev, isOnline: true, isOfflineFallback: false }));
    };
    const handleOffline = () => {
      setState(prev => ({ ...prev, isOnline: false, isOfflineFallback: true }));
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Service Worker update detection
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const checkForUpdate = async () => {
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (!registration) return;

        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (!newWorker) return;

          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              setState(prev => ({ ...prev, hasUpdate: true }));
            }
          });
        });
      } catch {
        // SW not supported or not registered
      }
    };

    checkForUpdate();

    // Troca do service worker que controla a página: recarrega para usar a versão nova.
    // Só quando a página já tinha um service worker (atualização). Na primeira visita o
    // service worker assume a página sem recarregar (não interrompe, por exemplo, o
    // cliente montando o pedido na loja).
    const hadController = !!navigator.serviceWorker.controller;
    let refreshing = false;
    const handleControllerChange = () => {
      if (refreshing || !hadController) return;
      refreshing = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
    };
  }, []);

  /** Apply pending update (reload) */
  const applyUpdate = useCallback(async () => {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration?.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
  }, []);

  /** Dismiss update notification */
  const dismissUpdate = useCallback(() => {
    setState(prev => ({ ...prev, hasUpdate: false }));
  }, []);

  return {
    ...state,
    applyUpdate,
    dismissUpdate,
  };
}
